import type { DefaultTheme, FlattenInterpolation, ThemeProps } from 'styled-components/macro';
import styled, { css } from 'styled-components/macro';

type ButtonStyledT = {
  hasIcon: boolean;
  isLoading: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'contained' | 'outline' | 'outline-rounded' | 'text';
  iconHoverColor?: 'black' | 'white' | 'darkBlue' | 'blue';
};

const contained = css`
  color: ${({ theme }): string => theme.colors.white};
  border: ${({ theme }): string => theme.borders.xs};
  border-color: ${({ theme }): string => theme.colors.primary.variant16};
  background-color: ${({ theme }): string => theme.colors.primary.variant16};
  border-radius: ${({ theme }): string => theme.radius.sm};

  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }): string => theme.colors.secondary.variant5};
      border-color: ${({ theme }): string => theme.colors.secondary.variant5};
    }
  }
`;

const outline = css`
  color: ${({ theme }): string => theme.colors.primary.variant1};
  border: ${({ theme }): string => `${theme.borders.xs} ${theme.colors.rgba.variant5}`};
  background-color: ${({ theme }): string => theme.colors.white};
  border-radius: ${({ theme }): string => theme.radius.sm};

  @media (hover: hover) {
    &:hover {
      color: ${({ theme }): string => theme.colors.secondary.variant5};
      border-color: ${({ theme }): string => theme.colors.secondary.variant5};
    }
  }
`;

const outlineRounded = css`
  color: ${({ theme }): string => theme.colors.rgba.variant4};
  border: ${({ theme }): string => `${theme.borders.xs} ${theme.colors.primary.variant6}`};
  background-color: ${({ theme }): string => theme.colors.white};
  border-radius: ${({ theme }): string => theme.radius['2xl']};

  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }): string => theme.colors.secondary.variant3};
      border-color: ${({ theme }): string => theme.colors.secondary.variant5};
      color: ${({ theme }): string => theme.colors.secondary.variant5};
    }
  }

  &:active {
    background-color: ${({ theme }): string => theme.colors.secondary.variant3};
    border-color: ${({ theme }): string => theme.colors.primary.variant6};
  }
`;

const text = css`
  border: ${({ theme }): string => theme.borders.none};
  color: ${({ theme }): string => theme.colors.primary.variant1};
  background-color: ${({ theme }): string => theme.colors.transparent};

  &:disabled {
    color: ${({ theme }): string => theme.colors.primary.variant7};
  }
`;

const xs = css`
  padding: 0 0.75rem;
  height: 28px;
  font-size: ${({ theme }): string => theme.fontSize.sm};
  font-weight: ${({ theme }): number => theme.fontWeight.normal};
`;

const sm = css`
  padding: 0 0.875rem;
  height: 28px;
  font-size: ${({ theme }): string => theme.fontSize.sm};
  font-weight: ${({ theme }): number => theme.fontWeight.normal};
`;

const md = css`
  padding: 0 0.875rem;
  height: 30px;
  font-size: ${({ theme }): string => theme.fontSize.lg};
  font-weight: ${({ theme }): number => theme.fontWeight.semibold};
`;

const lg = css`
  padding: 0 0.875rem;
  height: 36px;
  font-size: ${({ theme }): string => theme.fontSize.sm};
  font-weight: ${({ theme }): number => theme.fontWeight.normal};
`;

const xl = css`
  padding: 0 1.25rem;
  height: 38px;
  font-size: ${({ theme }): string => theme.fontSize.lg};
  font-weight: ${({ theme }): number => theme.fontWeight.semibold};
`;

const whiteHover = css`
  fill: ${({ theme }): string => theme.colors.white};
`;

const blackHover = css`
  fill: ${({ theme }): string => theme.colors.black};
`;

const darkBlueHover = css`
  fill: ${({ theme }): string => theme.colors.primary.variant1};
`;

const blueHover = css`
  fill: ${({ theme }): string => theme.colors.secondary.variant5};
`;

const iconBtnCss = css<ButtonStyledT>`
  padding-left: 0.625rem;
  padding-right: 1rem;

  @media (hover: hover) {
    &:hover {
      svg path {
        ${({ iconHoverColor }): FlattenInterpolation<ThemeProps<DefaultTheme>> =>
          iconHoverColor ? hoverColor[iconHoverColor] : hoverColor['blue']};
      }
    }
  }
`;

const loadingCss = css`
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.7;
`;

const hoverColor = {
  white: whiteHover,
  black: blackHover,
  darkBlue: darkBlueHover,
  blue: blueHover,
};

const buttonVariant = {
  contained,
  outline,
  'outline-rounded': outlineRounded,
  text,
};

const buttonSize = {
  xs,
  sm,
  md,
  lg,
  xl,
};

export const ButtonStyled = styled.button<ButtonStyledT>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: 0.3s;

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  ${({ variant = 'contained' }): FlattenInterpolation<ThemeProps<DefaultTheme>> =>
    buttonVariant[variant]}
  ${({ size = 'md' }): FlattenInterpolation<ThemeProps<DefaultTheme>> => buttonSize[size]}
  ${({ hasIcon }): ReturnType<typeof css> =>
    hasIcon ? (iconBtnCss as ReturnType<typeof css>) : css``}
  ${({ isLoading }): ReturnType<typeof css> => (isLoading ? loadingCss : css``)}
`;

export const IconWrapStyled = styled.span`
  display: inline-flex;
  margin-right: 0.625rem;
`;
